import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  Search, 
  Loader2,
  Package,
  RefreshCw,
  Download,
  Filter
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StockOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    plant: '',
    storageLocation: '',
    includeZero: false
  });
  const [plants, setPlants] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [materialMap, setMaterialMap] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [stockRes, plantRes, materialRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stock-overview?include_zero_stock=${filters.includeZero}`),
        axios.get(`${BACKEND_URL}/api/material-management/plants`),
        axios.get(`${BACKEND_URL}/api/materials`)
      ]);
      
      // Create material description map
      const matMap = {};
      (materialRes.data || []).forEach(m => {
        matMap[m.material_number] = {
          description: m.description,
          base_uom: m.base_uom,
          currency: m.accounting?.currency || 'USD'
        };
      });
      setMaterialMap(matMap);
      
      // Enrich stock data with material descriptions
      const enrichedStocks = (stockRes.data || []).map(s => ({
        ...s,
        material_description: matMap[s.material_id]?.description || s.material_description || '-'
      }));
      
      setStocks(enrichedStocks);
      setFilteredStocks(enrichedStocks);
      setPlants(plantRes.data || []);
    } catch (error) {
      console.error('Error loading stocks:', error);
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }, [filters.includeZero]);

  const filterStocks = useCallback(() => {
    let filtered = [...stocks];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.material_id?.toLowerCase().includes(term) ||
        s.material_description?.toLowerCase().includes(term) ||
        s.plant?.toLowerCase().includes(term)
      );
    }
    
    if (filters.plant) {
      filtered = filtered.filter(s => s.plant === filters.plant);
    }
    
    if (filters.storageLocation) {
      filtered = filtered.filter(s => s.storage_location === filters.storageLocation);
    }
    
    setFilteredStocks(filtered);
  }, [stocks, searchTerm, filters.plant, filters.storageLocation]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    filterStocks();
  }, [filterStocks]);

  const handleRefresh = () => {
    loadData();
    toast.success('Stock data refreshed');
  };

  const formatNumber = (num, decimals = 2) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num || 0);
  };

  const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(value || 0);
  };

  const getPriceControlLabel = (pc) => {
    return pc === 'V' ? 'Moving Avg' : pc === 'S' ? 'Standard' : pc;
  };

  // Calculate totals
  const totals = filteredStocks.reduce((acc, s) => ({
    totalQty: acc.totalQty + (s.total_stock || 0),
    totalValue: acc.totalValue + (s.total_value || 0)
  }), { totalQty: 0, totalValue: 0 });

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/inventory')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Stock Overview</h1>
              <p className="text-sm text-gray-500">MMBE - Stock by Material & Plant</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" /> Export
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search material, description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-40">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Plant</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={filters.plant}
                onChange={(e) => {
                  setFilters({ ...filters, plant: e.target.value });
                  filterStocks();
                }}
              >
                <option value="">All Plants</option>
                {plants.map(p => (
                  <option key={p.plant_id} value={p.plant_id}>{p.plant_id} - {p.plant_name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeZero"
                checked={filters.includeZero}
                onChange={(e) => {
                  setFilters({ ...filters, includeZero: e.target.checked });
                  loadData();
                }}
                className="rounded"
              />
              <label htmlFor="includeZero" className="text-sm text-gray-700">Include Zero Stock</label>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600 font-medium">Total Items</p>
            <p className="text-2xl font-bold text-orange-900">{filteredStocks.length}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600 font-medium">Total Quantity</p>
            <p className="text-2xl font-bold text-green-900">{formatNumber(totals.totalQty, 0)}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600 font-medium">Total Value</p>
            <p className="text-2xl font-bold text-orange-900">{formatCurrency(totals.totalValue)}</p>
          </div>
        </div>

        {/* Stock Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
              <span className="ml-2 text-gray-600">Loading stock data...</span>
            </div>
          ) : filteredStocks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Package className="h-12 w-12 mb-4 opacity-50" />
              <p>No stock records found</p>
              <Button variant="link" onClick={() => navigate('/inventory/initial-stock')} className="mt-2">
                Post Initial Stock
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Material</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Plant</TableHead>
                  <TableHead>SLoc</TableHead>
                  <TableHead className="text-right">Unrestricted</TableHead>
                  <TableHead className="text-right">Quality</TableHead>
                  <TableHead className="text-right">Blocked</TableHead>
                  <TableHead className="text-right">Total Qty</TableHead>
                  <TableHead className="text-center">UoM</TableHead>
                  <TableHead className="text-center">Price Ctrl</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStocks.map((stock) => (
                  <TableRow
                    key={`${stock.material_id}-${stock.plant}-${stock.storage_location}`}
                    className="hover:bg-gray-50"
                  >
                    <TableCell className="font-medium text-orange-600">{stock.material_id}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{stock.material_description || '-'}</TableCell>
                    <TableCell>{stock.plant}</TableCell>
                    <TableCell>{stock.storage_location}</TableCell>
                    <TableCell className="text-right">{formatNumber(stock.unrestricted_stock, 3)}</TableCell>
                    <TableCell className="text-right">{formatNumber(stock.quality_stock, 3)}</TableCell>
                    <TableCell className="text-right">{formatNumber(stock.blocked_stock, 3)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatNumber(stock.total_stock, 3)}</TableCell>
                    <TableCell className="text-center">{stock.base_unit}</TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        stock.price_control === 'V' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {getPriceControlLabel(stock.price_control)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(stock.price_control === 'V' ? stock.moving_avg_price : stock.standard_price, stock.currency)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(stock.total_value, stock.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StockOverview;
